// Units as Apsio writes them (docs/brand.md, Type), the console's forms (apps/console/src/lib/format.ts):
// the admin's one module for durations, money, counts and percents. Pages import it rather than
// formatting on their own.
import { useI18n } from '../i18n'

type NumberFormat = (value: number, options?: Intl.NumberFormatOptions) => string

/**
 * 412 ms, 4.31 s, 38.2 s, 3 m 48 s, 9 h 35 min (Console D, APS-339): under a minute, three
 * significant digits; then minutes and seconds; past an hour, hours and minutes.
 */
export function duration(number: NumberFormat, ms: number): string {
  if (!Number.isFinite(ms)) return ''
  if (ms < 1000) return `${number(Math.round(ms))} ms`
  if (ms < 59_950)
    return `${number(ms / 1000, { minimumSignificantDigits: 3, maximumSignificantDigits: 3 })} s`
  // Whole seconds first, so 3 m 59.6 s reads 4 m, never 3 m 60 s.
  const total = Math.round(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  if (m < 60) return s ? `${m} m ${s} s` : `${m} m`
  const h = Math.floor(m / 60)
  return `${h} h ${m % 60} min`
}

/**
 * A share (0.813) as a percent with a no-break space before the sign, as the console: 81,3 %.
 * `sign` shows a plus for a change (+4,2 %), as Intl's signDisplay.
 */
export function percent(
  number: NumberFormat,
  share: number,
  max = 1,
  sign?: 'always' | 'exceptZero',
  min = 0,
): string {
  // A fall reads with a real minus (U+2212), not the hyphen Intl writes (Mosaic): −4,5 %.
  const text = number(share * 100, {
    maximumFractionDigits: max,
    minimumFractionDigits: Math.min(min, max),
    ...(sign ? { signDisplay: sign } : {}),
  }).replace('-', '\u2212')
  return `${text}\u00a0%`
}

type DateFormat = (value: string, options?: Intl.DateTimeFormatOptions) => string

/**
 * A calendar day (YYYY-MM-DD) on a chart's axis, as short as a tick needs: "18 out.", "Oct 18".
 * pt-BR's "de" goes, so neighbouring days never run into each other (Mosaic, APS-412).
 */
export function axisDay(date: DateFormat, day: string): string {
  return date(`${day}T12:00:00Z`, { day: 'numeric', month: 'short', timeZone: 'UTC' }).replace(
    ' de ',
    ' ',
  )
}

/**
 * An instant's day where a line is short (a strip's sub): "20 out.", "Oct 20". The year shows as
 * date() shows it, only outside the current year ("31 dez. de 2025": only the first "de" goes).
 */
export function shortDay(date: DateFormat, at: string): string {
  return date(at).replace(' de ', ' ')
}

/** A count in full below 10,000, then compact with a lowercase k (Console D): 14.2k. */
export function compact(number: NumberFormat, n: number): string {
  return n < 10_000
    ? number(Math.round(n))
    : number(n, { notation: 'compact', maximumFractionDigits: 1 }).replace(/K$/, 'k')
}

/** Minor units (Stripe's cents) as money in their own currency, never converted (D52). */
export function money(number: NumberFormat, minor: number, currency: string): string {
  return number(minor / 100, { style: 'currency', currency: currency.toUpperCase() })
}

/** The forms above with the staff member's locale: money, counts, percents, months. */
export function useFormat() {
  const { number, date } = useI18n()
  return {
    /** US dollars, up to `max` decimals. */
    usd: (n: number, max = 2) =>
      number(n, { style: 'currency', currency: 'USD', maximumFractionDigits: max }),
    /** Reais, whole. */
    brl: (n: number) => number(n, { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }),
    /** A dollar rate in reais, to four places: R$ 5,4127 (the PTAX's own precision). */
    brlRate: (rate: number) =>
      number(rate, {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }),
    money: (minor: number, currency: string) => money(number, minor, currency),
    /** Minor units as whole money, for a strip's line (Mosaic): US$ 474, R$ 1.317. */
    moneyWhole: (minor: number, currency: string) =>
      number(Math.round(minor / 100), {
        style: 'currency',
        currency: currency.toUpperCase(),
        maximumFractionDigits: 0,
      }),
    pct: (share: number, max = 1) => percent(number, share, max),
    /** A percent with one decimal always, for a column of them (Mosaic): 59,0 %, 66,3 %. */
    pct1: (share: number) => percent(number, share, 1, undefined, 1),
    /** A whole count: 130,000. */
    n: (n: number) => number(Math.round(n)),
    compact: (n: number) => compact(number, n),
    /** A `YYYY-MM` month (UTC) by name, as MonthChip says it: "outubro de 2026". */
    month: (ym: string) =>
      date(`${ym}-01T00:00:00Z`, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
  }
}
